import {
    IRiskAcceptanceReport,
    IResourceThreatStatusReport,
    IResourceBarrierStatusReport,
    IManualInteractionBarrierStatusReport,
    IResourceBarrierAnalysisReport,
    IResourceBarrierPFDReport,
    IResourceBarrierAnalysisGraphic,
    IRiskAcceptanceAndRiskLevelGraphic,
    IResourceConsequenceStatusReport,
    IMandatoryBarriersReport
} from 'plugin-codaf-risk-ext-bowtie/types/reports'
import { transformTSIntoNaturalDate } from 'plugin-codaf-risk-ext-bowtie/utils'
import { BOWTIE_BASE_URL, REPORT_EVENTS_ENDPOINTS } from 'plugin-codaf-risk-ext-bowtie/utils/constants'

export interface ITimeFilter {
    startDay: string
    startHour: string
    endDay: string
    endHour: string
    perPage?: number
    page?: number
    sortOrder?: string
    sortBy?: string
}

export interface IFilterBy {
    filter: string
    elements: string[]
}

export interface IBody {
    filters?: IFilterBy[]
}

export interface IResourceBarrierAnalysisReportRequest extends ITimeFilter {
    filterByResource?: string[]
    filterByBowtieCode?: string[]
    filterByBarrierId?: string[]
    filterByBarrierName?: string[]
    filterByCategory?: string[]
    filterByStatus?: string[]
    filterByDate?: string[]
    filterByTimeInterval?: string[]
    filterByTimeGathered?: string[]
    filterByResponsible?: string[]
    filterBySystemId?: string[]
}

export interface IResourceBarrierAnalysisGraphicRequest extends ITimeFilter {
    filterByUniqueId?: string
}

export interface IResourceBarrierPFDReportRequest extends ITimeFilter {
    filterByResource?: string[]
    filterByBowtieCode?: string[]
    filterByBarrierId?: string[]
    filterByBarrierName?: string[]
    filterByInitialPfd?: string[]
    filterByFinalPfd?: string[]
    filterByThreshold?: string[]
    filterByStatus?: string[]
    filterByDate?: string[]
}

export interface IResourceBarrierStatusReportRequest extends ITimeFilter {
    filterByResource?: string[]
    filterByBowtieCode?: string[]
    filterByBarrierId?: string[]
    filterByBarrierName?: string[]
    filterByType?: string[]
    filterByEntity?: string[]
    filterByCategory?: string[]
    filterByStatus?: string[]
    filterByDate?: string[]
}

export interface IResourceThreatStatusReportRequest extends ITimeFilter {
    filterByResource?: string[]
    filterByBowtieCode?: string[]
    filterByThreatName?: string[]
    filterByInitialFrequency?: string[]
    filterByFinalFrequency?: string[]
    filterByDate?: string[]
}

export interface IResourceConsequenceStatusReportRequest extends ITimeFilter {
    filterByResource?: string[]
    filterByBowtieCode?: string[]
    filterByConsequenceName?: string[]
    filterByInitialFrequency?: string[]
    filterByFinalFrequency?: string[]
    filterByDate?: string[]
}

export interface IManualInteractionBarrierStatusReportRequest extends ITimeFilter {
    filterByResource?: string[]
    filterByBowtieCode?: string[]
    filterByBarrierId?: string[]
    filterByBarrierName?: string[]
    filterByInitialPfd?: string[]
    filterByFinalPfd?: string[]
    filterByMOC?: string[]
    filterByResponsible?: string[]
    filterByStatus?: string[]
    filterByDate?: string[]
}

export interface ICurrentMandatoryBarrierExportRequest extends ITimeFilter {
    filterByResource?: string[]
    filterByBowtieCode?: string[]
    filterByBarrierId?: string[]
    filterByBarrierName?: string[]
    filterBySafetyCritical?: string[]
    filterByResponsible?: string[]
}

export interface IRiskAcceptanceReportRequest extends ITimeFilter {
    filterByResource?: string[]
    filterByBowtieCode?: string[]
    filterByBowtieName?: string[]
    filterByRiskLevelStatus?: string[]
    filterByDate?: string[]
}

export const getRiskAcceptanceAndRiskLevel = async (
    params?: IRiskAcceptanceReportRequest
): Promise<IRiskAcceptanceReport> => {
    const qs = new URLSearchParams({
        limit: '20',
        offset: ((params?.page ?? 1) - 1).toString() ?? '',
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByBowtieName: params.filterByBowtieName ? params.filterByBowtieName.toString() : '',
        filterByRiskLevelStatus: params.filterByRiskLevelStatus ? params.filterByRiskLevelStatus.toString() : '',
        filterByDate: params.filterByDate ? params.filterByDate.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.riskAcceptanceAndRiskLevel}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

export const exportRiskAcceptanceAndRiskLevel = async (params?: IRiskAcceptanceReportRequest): Promise<void> => {
    const qs = new URLSearchParams({
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByBowtieName: params.filterByBowtieName ? params.filterByBowtieName.toString() : '',
        filterByRiskLevelStatus: params.filterByRiskLevelStatus ? params.filterByRiskLevelStatus.toString() : '',
        filterByDate: params.filterByDate ? params.filterByDate.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.riskAcceptanceAndRiskLevelToCsv}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    })
        .then(async response => {
            if (response.ok) {
                return response.blob()
            } else {
                let jsonData
                try {
                    jsonData = await response.json()
                } catch (err) {
                    jsonData = undefined
                }
                throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
            }
        })
        .then(blob => {
            try {
                const time = new Date()

                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.setAttribute('href', url)
                a.setAttribute('download', `risk acceptance ${transformTSIntoNaturalDate(time.getTime())}.csv`)
                a.click()
                URL.revokeObjectURL(url)
            } catch (error) {
                console.log(error)
            }
        })
}

export const getResourceThreatStatusEvent = async (
    params?: IResourceThreatStatusReportRequest
): Promise<IResourceThreatStatusReport> => {
    const qs = new URLSearchParams({
        limit: '20',
        offset: ((params?.page ?? 1) - 1).toString() ?? '',
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByThreatName: params.filterByThreatName ? params.filterByThreatName.toString() : '',
        filterByInitialFrequency: params.filterByInitialFrequency ? params.filterByInitialFrequency.toString() : '',
        filterByFinalFrequency: params.filterByFinalFrequency ? params.filterByFinalFrequency.toString() : '',
        filterByDate: params.filterByDate ? params.filterByDate.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.resourceThreatStatusEvent}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

export const exportResourceThreatStatus = async (params?: IResourceThreatStatusReportRequest): Promise<void> => {
    const qs = new URLSearchParams({
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByThreatName: params.filterByThreatName ? params.filterByThreatName.toString() : '',
        filterByInitialFrequency: params.filterByInitialFrequency ? params.filterByInitialFrequency.toString() : '',
        filterByFinalFrequency: params.filterByFinalFrequency ? params.filterByFinalFrequency.toString() : '',
        filterByDate: params.filterByDate ? params.filterByDate.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.resourceThreatStatusEventToCsv}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    })
        .then(async response => {
            if (response.ok) {
                return response.blob()
            } else {
                let jsonData
                try {
                    jsonData = await response.json()
                } catch (err) {
                    jsonData = undefined
                }
                throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
            }
        })
        .then(blob => {
            try {
                const time = new Date()

                // link.href = url

                // link.setAttribute(
                //     'download',

                // )
                // document.body.appendChild(link)

                // link.click()

                // link.parentNode.removeChild(link)
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.setAttribute('href', url)
                a.setAttribute('download', `resource threat status ${transformTSIntoNaturalDate(time.getTime())}.csv`)
                a.click()
                URL.revokeObjectURL(url)
            } catch (error) {
                console.log(error)
            }
        })
}

export const getResourceConsequenceStatusEvent = async (
    params?: IResourceConsequenceStatusReportRequest
): Promise<IResourceConsequenceStatusReport> => {
    const qs = new URLSearchParams({
        limit: '20',
        offset: ((params?.page ?? 1) - 1).toString() ?? '',
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByConsequenceName: params.filterByConsequenceName ? params.filterByConsequenceName.toString() : '',
        filterByInitialFrequency: params.filterByInitialFrequency ? params.filterByInitialFrequency.toString() : '',
        filterByFinalFrequency: params.filterByFinalFrequency ? params.filterByFinalFrequency.toString() : '',
        filterByDate: params.filterByDate ? params.filterByDate.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.resourceConsequenceStatusEvent}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

export const exportResourceConsequenceStatus = async (
    params?: IResourceConsequenceStatusReportRequest
): Promise<void> => {
    const qs = new URLSearchParams({
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByConsequenceName: params.filterByConsequenceName ? params.filterByConsequenceName.toString() : '',
        filterByInitialFrequency: params.filterByInitialFrequency ? params.filterByInitialFrequency.toString() : '',
        filterByFinalFrequency: params.filterByFinalFrequency ? params.filterByFinalFrequency.toString() : '',
        filterByDate: params.filterByDate ? params.filterByDate.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.resourceConsequenceStatusEventToCsv}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    })
        .then(async response => {
            if (response.ok) {
                return response.blob()
            } else {
                let jsonData
                try {
                    jsonData = await response.json()
                } catch (err) {
                    jsonData = undefined
                }
                throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
            }
        })
        .then(blob => {
            try {
                const time = new Date()

                // link.href = url

                // link.setAttribute(
                //     'download',

                // )
                // document.body.appendChild(link)

                // link.click()

                // link.parentNode.removeChild(link)
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.setAttribute('href', url)
                a.setAttribute(
                    'download',
                    `resource consequence status ${transformTSIntoNaturalDate(time.getTime())}.csv`
                )
                a.click()
                URL.revokeObjectURL(url)
            } catch (error) {
                console.log(error)
            }
        })
}

export const getResourceBarrierStatusEvent = async (
    params?: IResourceBarrierStatusReportRequest
): Promise<IResourceBarrierStatusReport> => {
    const qs = new URLSearchParams({
        limit: '20',
        offset: ((params?.page ?? 1) - 1).toString() ?? '',
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByBarrierId: params.filterByBarrierId ? params.filterByBarrierId.toString() : '',
        filterByBarrierName: params.filterByBarrierName ? params.filterByBarrierName.toString() : '',
        filterByType: params.filterByType ? params.filterByType.toString() : '',
        filterByEntity: params.filterByEntity ? params.filterByEntity.toString() : '',
        filterByCategory: params.filterByCategory ? params.filterByCategory.toString() : '',
        filterByStatus: params.filterByStatus ? params.filterByStatus.toString() : '',
        filterByDate: params.filterByDate ? params.filterByDate.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.resourceBarrierStatusEvent}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

export const exportResourceBarrierStatus = async (params?: IResourceBarrierStatusReportRequest): Promise<void> => {
    const qs = new URLSearchParams({
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByBarrierId: params.filterByBarrierId ? params.filterByBarrierId.toString() : '',
        filterByBarrierName: params.filterByBarrierName ? params.filterByBarrierName.toString() : '',
        filterByType: params.filterByType ? params.filterByType.toString() : '',
        filterByEntity: params.filterByEntity ? params.filterByEntity.toString() : '',
        filterByCategory: params.filterByCategory ? params.filterByCategory.toString() : '',
        filterByStatus: params.filterByStatus ? params.filterByStatus.toString() : '',
        filterByDate: params.filterByDate ? params.filterByDate.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.resourceBarrierStatusEventToCsv}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    })
        .then(async response => {
            if (response.ok) {
                return response.blob()
            } else {
                let jsonData
                try {
                    jsonData = await response.json()
                } catch (err) {
                    jsonData = undefined
                }
                throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
            }
        })
        .then(blob => {
            try {
                const time = new Date()

                // link.href = url

                // link.setAttribute(
                //     'download',

                // )
                // document.body.appendChild(link)

                // link.click()

                // link.parentNode.removeChild(link)
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.setAttribute('href', url)
                a.setAttribute('download', `resource barrier status ${transformTSIntoNaturalDate(time.getTime())}.csv`)
                a.click()
                URL.revokeObjectURL(url)
            } catch (error) {
                console.log(error)
            }
        })
}

export const getResourceBarrierPFD = async (
    params?: IResourceBarrierPFDReportRequest
): Promise<IResourceBarrierPFDReport> => {
    const qs = new URLSearchParams({
        limit: '20',
        offset: ((params?.page ?? 1) - 1).toString() ?? '',
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByBarrierId: params.filterByBarrierId ? params.filterByBarrierId.toString() : '',
        filterByBarrierName: params.filterByBarrierName ? params.filterByBarrierName.toString() : '',
        filterByInitialPfd: params.filterByInitialPfd ? params.filterByInitialPfd.toString() : '',
        filterByFinalPfd: params.filterByFinalPfd ? params.filterByFinalPfd.toString() : '',
        filterByThreshold: params.filterByThreshold ? params.filterByThreshold.toString() : '',
        filterByStatus: params.filterByStatus ? params.filterByStatus.toString() : '',
        filterByDate: params.filterByDate ? params.filterByDate.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.resourceBarrierPFD}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

export const exportResourceBarrierPFD = async (params?: IResourceBarrierPFDReportRequest): Promise<void> => {
    const qs = new URLSearchParams({
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByBarrierId: params.filterByBarrierId ? params.filterByBarrierId.toString() : '',
        filterByBarrierName: params.filterByBarrierName ? params.filterByBarrierName.toString() : '',
        filterByInitialPfd: params.filterByInitialPfd ? params.filterByInitialPfd.toString() : '',
        filterByFinalPfd: params.filterByFinalPfd ? params.filterByFinalPfd.toString() : '',
        filterByThreshold: params.filterByThreshold ? params.filterByThreshold.toString() : '',
        filterByStatus: params.filterByStatus ? params.filterByStatus.toString() : '',
        filterByDate: params.filterByDate ? params.filterByDate.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.resourceBarrierPFDToCsv}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    })
        .then(async response => {
            if (response.ok) {
                return response.blob()
            } else {
                let jsonData
                try {
                    jsonData = await response.json()
                } catch (err) {
                    jsonData = undefined
                }
                throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
            }
        })
        .then(blob => {
            try {
                const time = new Date()

                // link.href = url

                // link.setAttribute(
                //     'download',

                // )
                // document.body.appendChild(link)

                // link.click()

                // link.parentNode.removeChild(link)
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.setAttribute('href', url)
                a.setAttribute('download', `resource barrier PFD ${transformTSIntoNaturalDate(time.getTime())}.csv`)
                a.click()
                URL.revokeObjectURL(url)
            } catch (error) {
                console.log(error)
            }
        })
}

export const getManualInteractionBarrierStatus = async (
    params?: IManualInteractionBarrierStatusReportRequest
): Promise<IManualInteractionBarrierStatusReport> => {
    const qs = new URLSearchParams({
        limit: '20',
        offset: ((params?.page ?? 1) - 1).toString() ?? '',
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByBarrierId: params.filterByBarrierId ? params.filterByBarrierId.toString() : '',
        filterByBarrierName: params.filterByBarrierName ? params.filterByBarrierName.toString() : '',
        filterByInitialPfd: params.filterByInitialPfd ? params.filterByInitialPfd.toString() : '',
        filterByFinalPfd: params.filterByFinalPfd ? params.filterByFinalPfd.toString() : '',
        filterByMOC: params.filterByMOC ? params.filterByMOC.toString() : '',
        filterByResponsible: params.filterByResponsible ? params.filterByResponsible.toString() : '',
        filterByStatus: params.filterByStatus ? params.filterByStatus.toString() : '',
        filterByDate: params.filterByDate ? params.filterByDate.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.manualInteractionBarrierStatus}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

export const exportManualInteractionBarrierStatus = async (
    params?: IManualInteractionBarrierStatusReportRequest
): Promise<void> => {
    const qs = new URLSearchParams({
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByBarrierId: params.filterByBarrierId ? params.filterByBarrierId.toString() : '',
        filterByBarrierName: params.filterByBarrierName ? params.filterByBarrierName.toString() : '',
        filterByInitialPfd: params.filterByInitialPfd ? params.filterByInitialPfd.toString() : '',
        filterByFinalPfd: params.filterByFinalPfd ? params.filterByFinalPfd.toString() : '',
        filterByMOC: params.filterByMOC ? params.filterByMOC.toString() : '',
        filterByResponsible: params.filterByResponsible ? params.filterByResponsible.toString() : '',
        filterByStatus: params.filterByStatus ? params.filterByStatus.toString() : '',
        filterByDate: params.filterByDate ? params.filterByDate.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.manualInteractionBarrierStatusToCsv}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    })
        .then(async response => {
            if (response.ok) {
                return response.blob()
            } else {
                let jsonData
                try {
                    jsonData = await response.json()
                } catch (err) {
                    jsonData = undefined
                }
                throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
            }
        })
        .then(blob => {
            try {
                const time = new Date()

                // link.href = url

                // link.setAttribute(
                //     'download',

                // )
                // document.body.appendChild(link)

                // link.click()

                // link.parentNode.removeChild(link)
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.setAttribute('href', url)
                a.setAttribute(
                    'download',
                    `manual interaction barrier status ${transformTSIntoNaturalDate(time.getTime())}.csv`
                )
                a.click()
                URL.revokeObjectURL(url)
            } catch (error) {
                console.log(error)
            }
        })
}

export const getCurrentMandatoryBarrierEvent = async (
    params?: ICurrentMandatoryBarrierExportRequest
): Promise<IMandatoryBarriersReport> => {
    const qs = new URLSearchParams({
        limit: '20',
        offset: ((params?.page ?? 1) - 1).toString() ?? '',
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByBarrierId: params.filterByBarrierId ? params.filterByBarrierId.toString() : '',
        filterByBarrierName: params.filterByBarrierName ? params.filterByBarrierName.toString() : '',
        filterByType: params.filterBySafetyCritical ? params.filterBySafetyCritical.toString() : '',
        filterByEntity: params.filterByResponsible ? params.filterByResponsible.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.currentMandatoryBarrier}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

export const exportCurrentMandatoryBarrier = async (params?: ICurrentMandatoryBarrierExportRequest): Promise<void> => {
    const qs = new URLSearchParams({
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByBarrierId: params.filterByBarrierId ? params.filterByBarrierId.toString() : '',
        filterByBarrierName: params.filterByBarrierName ? params.filterByBarrierName.toString() : '',
        filterBySafetyCritical: params.filterBySafetyCritical ? params.filterBySafetyCritical.toString() : '',
        filterByResponsible: params.filterByResponsible ? params.filterByResponsible.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.currentMandatoryBarrierToCsv}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    })
        .then(async response => {
            if (response.ok) {
                return response.blob()
            } else {
                let jsonData
                try {
                    jsonData = await response.json()
                } catch (err) {
                    jsonData = undefined
                }
                throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
            }
        })
        .then(blob => {
            try {
                const time = new Date()

                // link.href = url

                // link.setAttribute(
                //     'download',

                // )
                // document.body.appendChild(link)

                // link.click()

                // link.parentNode.removeChild(link)
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.setAttribute('href', url)
                a.setAttribute(
                    'download',
                    `current mandatory barrier ${transformTSIntoNaturalDate(time.getTime())}.csv`
                )
                a.click()
                URL.revokeObjectURL(url)
            } catch (error) {
                console.log(error)
            }
        })
}

export const getResourceBarrierAnalysis = async (
    params?: IResourceBarrierAnalysisReportRequest
): Promise<IResourceBarrierAnalysisReport> => {
    const qs = new URLSearchParams({
        limit: '20',
        offset: ((params?.page ?? 1) - 1).toString() ?? '',
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByBarrierId: params.filterByBarrierId ? params.filterByBarrierId.toString() : '',
        filterByBarrierName: params.filterByBarrierName ? params.filterByBarrierName.toString() : '',
        filterBySystemId: params.filterBySystemId ? params.filterBySystemId.toString() : '',
        filterByCategory: params.filterByCategory ? params.filterByCategory.toString() : '',
        filterByStatus: params.filterByStatus ? params.filterByStatus.toString() : '',
        filterByResponsible: params.filterByResponsible ? params.filterByResponsible.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.resourceBarrierAnalysis}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

export const exportResourceBarrierAnalysis = async (params?: IResourceBarrierAnalysisReportRequest): Promise<void> => {
    const qs = new URLSearchParams({
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        sortOrder: params.sortOrder ?? '',
        sortBy: params.sortBy ?? '',
        filterByResource: params.filterByResource ? params.filterByResource.toString() : '',
        filterByBowtieCode: params.filterByBowtieCode ? params.filterByBowtieCode.toString() : '',
        filterByBarrierId: params.filterByBarrierId ? params.filterByBarrierId.toString() : '',
        filterByBarrierName: params.filterByBarrierName ? params.filterByBarrierName.toString() : '',
        filterBySystemId: params.filterBySystemId ? params.filterBySystemId.toString() : '',
        filterByCategory: params.filterByCategory ? params.filterByCategory.toString() : '',
        filterByStatus: params.filterByStatus ? params.filterByStatus.toString() : '',
        filterByResponsible: params.filterByResponsible ? params.filterByResponsible.toString() : ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.resourceBarrierAnalysisToCsv}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    })
        .then(async response => {
            if (response.ok) {
                return response.blob()
            } else {
                let jsonData
                try {
                    jsonData = await response.json()
                } catch (err) {
                    jsonData = undefined
                }
                throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
            }
        })
        .then(blob => {
            try {
                const time = new Date()

                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.setAttribute('href', url)
                a.setAttribute(
                    'download',
                    `resource barrier analysis ${transformTSIntoNaturalDate(time.getTime())}.csv`
                )
                a.click()
                URL.revokeObjectURL(url)
            } catch (error) {
                console.log(error)
            }
        })
}

export const getResourceBarrierAnalysisGraph = async (
    params?: IResourceBarrierAnalysisGraphicRequest
): Promise<IResourceBarrierAnalysisGraphic> => {
    const qs = new URLSearchParams({
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        filterByUniqueId: params?.filterByUniqueId ?? ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.resourceBarrierAnalysisGraph}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}

export const getRiskAcceptanceAndRiskLevelGraph = async (
    params?: IResourceBarrierAnalysisGraphicRequest
): Promise<IRiskAcceptanceAndRiskLevelGraphic> => {
    const qs = new URLSearchParams({
        startDay: params?.startDay?.toString() ?? '',
        startHour: params.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        filterByUniqueId: params?.filterByUniqueId ?? ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/reports/${REPORT_EVENTS_ENDPOINTS.riskAcceptanceAndRiskLevelGraph}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
        // body: JSON.stringify(body)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
        }
    })
}
